import { AuthService } from './../../services/auth.service';
import { Component, EventEmitter, Input, Output, OnInit, ViewChild, forwardRef } from '@angular/core';
import { ClientService } from '../../services/client/client.service';
import { ClientDTO, PagedResultDTOOfClientDTO } from '../../models/models.swagger.generated';
import { MultiSelectItem } from '../autocomplete/multi-select-item';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgIf } from '@angular/common';

@Component({
    selector: 'app-client-autocomplete',
    templateUrl: './client-autocomplete.component.html',
    styleUrls: ['./client-autocomplete.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ClientAutocompleteComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ClientAutocompleteComponent),
            multi: true
        }
    ],
    standalone: true,
    imports: [NgIf, AutocompleteComponent]
})

export class ClientAutocompleteComponent implements ControlValueAccessor, OnInit {
  @ViewChild(AutocompleteComponent)
  public autoCompleteComponent: AutocompleteComponent;

  @Output() public selectedClientChange: EventEmitter<ClientDTO> = new EventEmitter<ClientDTO>();

  @Input() public placeholder = 'i18n.shared.search-company';

  @Input() public set selectedClient(client: ClientDTO) {
    if (this._selectedClient?.id != client?.id) {
      this._selectedClient = client;
      if (client && !this.clientsCache.find(c => c.id === client.id)) {
        this.clientsCache.push(client);
        this.autoCompleteComponent?.refresh();
      }
      this.selectedClientChange.emit(client);
      this.onChange(client);
    }
  }

  @Input() public showSystem = false;

  public get isFormComponent(): boolean {
    return this._control != null;
  }

  private systemClient = { id: 0, name: this.translateService.instant('i18n.base.client-dropdown.system') } as ClientDTO;
  public get selectedClientId(): number {
    if (this._selectedClient === null) {
      return null;
    }
    return this._selectedClient?.id;
  }

  public set selectedClientId(value: number) {
    if (this.selectedClientId != value) {
      let client = this.getClientById(value);
      if(value === 0 && this.showSystem){
        client = this.systemClient;
      }
      this._selectedClient = client;
      this.selectedClientChange.emit(client);
      this.onChange(client);
      this.onTouched();
    }
  }

  public get isClientAdminView(): boolean {
    return this.authService.hasUserClientAssigned();
  }

  public fields: Object = { text: 'name', value: 'id' };
  public selectedClientName: string;
  public isDisabled = false;

  private _selectedClient: ClientDTO;
  private clientsCache: ClientDTO[] = [];
  private _control: AbstractControl;

  constructor(
    private clientService: ClientService,
    private authService: AuthService,
    private translateService: TranslateService
  ) {
  }

  public onChange: any = () => {};
  public onTouched: any = () => {};

  public writeValue(client: ClientDTO): void {
    this.selectedClient = client;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public validate(control: FormControl) {
    this._control = control;
    return null;
  }

  ngOnInit(): void {
    if(this.authService.isUserClientUser()){
      this.selectedClientName = this.getClientsDisplayName(this.authService.getUserInfo().client);
    }
    if (this.clientService.selectedClient?.id === 0 && !this.showSystem) {
      this.clientService.selectedClient = null;
    }
  }

  public searchClientData(searchText: string): Observable<MultiSelectItem[]> {
    return this.clientService.searchClients(searchText, 20).pipe(map((clientPaged: PagedResultDTOOfClientDTO) => {
      this.clientsCache = clientPaged.items;
      if (this.showSystem && this.authService.isUserAdmin()) {
        this.clientsCache.push(this.systemClient);
      }
      const clientId = this._selectedClient?.id;
      if (clientId && !this.clientsCache.find(c => c.id === clientId)) {
        this.clientsCache.push(this._selectedClient);
      }
      return this.clientsCache.map((client: ClientDTO) => {
        return { id: client.id, name: this.getClientsDisplayName(client) };
      });
    }));
  }

  public focus(): void {
    this.autoCompleteComponent?.focus();
  }

  private getClientById(id: number) {
    if (!id) {
      return null;
    }
    return this.clientsCache.find(c => c.id == id);
  }

  private getClientsDisplayName(client: ClientDTO) {
    if (!client) {
      return null;
    }
    return client.name;
  }
}
